import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Cookie, ShieldCheck, Settings, X } from "lucide-react";
import { useLang } from "../contexts/LangContext";

const LS_KEY = "a3k_consent";
const VISITOR_KEY = "a3k_visitor_id";

function getVisitorId() {
  let v = localStorage.getItem(VISITOR_KEY);
  if (!v) { v = Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem(VISITOR_KEY, v); }
  return v;
}

export default function CookieBanner() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem(LS_KEY);
    if (!stored) setOpen(true);
  }, []);

  const save = async (choice, a = analytics, m = marketing) => {
    const payload = {
      choice, functional: true, analytics: a, marketing: m,
      ts: new Date().toISOString(),
    };
    localStorage.setItem(LS_KEY, JSON.stringify(payload));
    api.post("/consent/log", { ...payload, visitor_id: getVisitorId() }).catch(() => {});
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div data-testid="cookie-banner" className="fixed inset-x-0 bottom-0 z-[90] p-3 md:p-5 pointer-events-none">
      <div className="max-w-4xl mx-auto bg-[#0a0a0a] text-white border border-[#C9A961]/40 rounded-sm shadow-2xl pointer-events-auto overflow-hidden">
        {/* Top gold bar */}
        <div className="h-1 bg-[#C9A961]" />
        <div className="p-5 md:p-6">
          <div className="flex items-start gap-4">
            <div className="hidden md:flex w-11 h-11 items-center justify-center bg-[#C9A961]/10 border border-[#C9A961]/30 rounded-sm shrink-0">
              <Cookie className="h-5 w-5 text-[#C9A961]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs uppercase tracking-[0.3em] text-[#C9A961] font-bold">{t("cookie.eyebrow")}</span>
                <ShieldCheck className="h-3.5 w-3.5 text-[#C9A961]" />
              </div>
              <h3 className="editorial-heading text-lg md:text-xl leading-tight">{t("cookie.title")}</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">
                {t("cookie.body")}{" "}
                {t("cookie.read")} <Link to="/cookie" className="underline hover:text-[#C9A961]">{t("cookie.policy")}</Link> · <Link to="/privacy" className="underline hover:text-[#C9A961]">{t("cookie.privacy_policy")}</Link>.
              </p>

              {customize && (
                <div data-testid="cookie-customize" className="mt-4 space-y-2 border-t border-white/10 pt-4">
                  <label className="flex items-center justify-between gap-3 py-1.5">
                    <div>
                      <div className="text-sm font-semibold">{t("cookie.essential")}</div>
                      <div className="text-xs text-white/50">{t("cookie.essential_desc")}</div>
                    </div>
                    <input type="checkbox" checked disabled className="accent-[#C9A961]" />
                  </label>
                  <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                    <div>
                      <div className="text-sm font-semibold">{t("cookie.analytics")}</div>
                      <div className="text-xs text-white/50">{t("cookie.analytics_desc")}</div>
                    </div>
                    <input data-testid="cookie-analytics" type="checkbox" checked={analytics} onChange={(e) => setAnalytics(e.target.checked)} className="accent-[#C9A961]" />
                  </label>
                  <label className="flex items-center justify-between gap-3 py-1.5 cursor-pointer">
                    <div>
                      <div className="text-sm font-semibold">{t("cookie.marketing")}</div>
                      <div className="text-xs text-white/50">{t("cookie.marketing_desc")}</div>
                    </div>
                    <input data-testid="cookie-marketing" type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="accent-[#C9A961]" />
                  </label>
                </div>
              )}

              <div className="mt-5 flex flex-wrap items-center gap-2">
                {!customize ? (
                  <>
                    <Button data-testid="cookie-accept-all" size="sm" onClick={() => save("accept_all", true, true)} className="bg-[#C9A961] hover:bg-white text-black font-semibold uppercase tracking-wider text-[11px]">
                      {t("cookie.accept_all")}
                    </Button>
                    <Button data-testid="cookie-reject-all" size="sm" variant="outline" onClick={() => save("reject_all", false, false)} className="border-white/30 text-white hover:bg-white hover:text-black uppercase tracking-wider text-[11px]">
                      {t("cookie.reject")}
                    </Button>
                    <Button data-testid="cookie-customize-btn" size="sm" variant="ghost" onClick={() => setCustomize(true)} className="text-white/70 hover:text-white gap-1.5 uppercase tracking-wider text-[11px]">
                      <Settings className="h-3.5 w-3.5" /> {t("cookie.customize")}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button data-testid="cookie-save-custom" size="sm" onClick={() => save("custom", analytics, marketing)} className="bg-[#C9A961] hover:bg-white text-black font-semibold uppercase tracking-wider text-[11px]">
                      {t("cookie.save")}
                    </Button>
                    <Button data-testid="cookie-accept-all-2" size="sm" variant="outline" onClick={() => save("accept_all", true, true)} className="border-white/30 text-white hover:bg-white hover:text-black uppercase tracking-wider text-[11px]">
                      {t("cookie.accept_all")}
                    </Button>
                  </>
                )}
              </div>
            </div>
            <button data-testid="cookie-close" onClick={() => save("reject_all", false, false)} aria-label={t("cookie.close")} className="text-white/40 hover:text-white p-1 shrink-0">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
